import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Button, Paper, Stack, TextField, Typography, MenuItem,
  IconButton, Grid, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import QuestionarioCampos from "../../components/QuestionarioCampos";
import LancamentosFixos, { montarLancamentos, valoresDeLancamentos } from "../../components/LancamentosFixos";
import CampoMoeda from "../../components/CampoMoeda";
import TabsBar from "../../components/TabsBar";
import DocumentosChecklist from "../../components/DocumentosChecklist";
import RecursoCandidato from "./RecursoCandidato";
import { agruparPorSecao, indexarPorCodigo, perguntasPendentes } from "../../utils/questionario";
import {
  minhaFicha, salvarMinhaFicha, enviarMinhaFicha, getTiposLancamento, getTiposDocumentoCand,
  listarAnexosCand, uploadAnexoCand, excluirAnexoCand, baixarAnexoCand, getAnexoBlobCand,
} from "../../services/candidatoService";

const novoFamiliar = () => ({ Nome: "", Parentesco: "", Idade: "", EstadoCivil: "", Escolaridade: "", Profissao: "", SalarioBruto: 0 });
const novoPatrimonio = () => ({ Tipo: "Imovel", Descricao: "", ValorMercado: 0, ValorParcela: 0, QtdParcelasAberto: "" });
const novoVeiculo = () => ({ Tipo: "Veiculo", Descricao: "", Ano: "" });
const novaSaude = () => ({ NomeCompleto: "", Parentesco: "", DoencaCronica: "", Deficiencia: "", EmTratamento: "" });

const BotaoAdd = ({ onClick }) => <Button size="small" startIcon={<AddIcon />} onClick={onClick}>Adicionar</Button>;
const Vazio = ({ texto }) => <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{texto}</Typography>;

export default function CandidatoFicha({ token, perguntas, aberta }) {
  const [aba, setAba] = useState(0);
  const [ficha, setFicha] = useState(null);
  const [responsavel, setResponsavel] = useState({ Nome: "", Cpf: "", Parentesco: "" });
  const [vinculoTipo, setVinculoTipo] = useState(""); // "RT" | "OUTRO"
  const [vinculoOutro, setVinculoOutro] = useState("");
  const [familia, setFamilia] = useState([]);
  const [patrimonio, setPatrimonio] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [saude, setSaude] = useState([]);
  const [valoresLanc, setValoresLanc] = useState({});
  const [respostas, setRespostas] = useState({});
  const [tiposR, setTiposR] = useState([]);
  const [tiposD, setTiposD] = useState([]);
  const [anexos, setAnexos] = useState([]);
  const [tiposDoc, setTiposDoc] = useState([]);
  const [enviada, setEnviada] = useState(false);

  const secoes = useMemo(() => agruparPorSecao(perguntas), [perguntas]);
  const byCodigo = useMemo(() => indexarPorCodigo(perguntas), [perguntas]);

  const carregarAnexos = useCallback(async (idFicha) => {
    const { data } = await listarAnexosCand(idFicha);
    setAnexos(data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [tr, td, tdoc] = await Promise.all([getTiposLancamento("R"), getTiposLancamento("D"), getTiposDocumentoCand()]);
        setTiposR(tr.data); setTiposD(td.data); setTiposDoc(tdoc.data);
        const { data } = await minhaFicha(token);
        setFicha(data.Ficha);
        setEnviada(data.Ficha.IdSituacaoInscricao === 2);
        setResponsavel(data.Responsavel || { Nome: "", Cpf: "", Parentesco: "" });
        const par = (data.Responsavel || {}).Parentesco || "";
        if (par === "Responsável/Tutor") { setVinculoTipo("RT"); setVinculoOutro(""); }
        else if (par) { setVinculoTipo("OUTRO"); setVinculoOutro(par); }
        setFamilia(data.ComposicaoFamiliar || []);
        const pats = data.Patrimonio || [];
        setPatrimonio(pats.filter((p) => p.Tipo !== "Veiculo"));
        setVeiculos(pats.filter((p) => p.Tipo === "Veiculo"));
        setSaude(data.SaudeFamiliar || []);
        setValoresLanc(valoresDeLancamentos(data.Lancamentos));
        const mapa = {};
        (data.Respostas || []).forEach((r) => { mapa[r.IdPergunta] = r.ValorBool ?? r.ValorNumero ?? r.ValorData ?? r.ValorTexto ?? r.IdOpcao ?? ""; });
        setRespostas(mapa);
        await carregarAnexos(data.Ficha.Id);
      } catch (e) {
        Swal.fire("Erro", e?.response?.data?.mensagem || e?.response?.data || e.message, "error");
      }
    })();
  }, [token, carregarAnexos]);

  const upd = (lista, set, i, campo, v) => set(lista.map((x, idx) => (idx === i ? { ...x, [campo]: v } : x)));
  const del = (lista, set, i) => set(lista.filter((_, idx) => idx !== i));
  const setResp = (id, v) => setRespostas((r) => ({ ...r, [id]: v }));

  const montarRespostas = () =>
    perguntas.map((p) => {
      const v = respostas[p.Id];
      const r = { IdPergunta: p.Id, ValorBool: null, ValorNumero: null, ValorTexto: null, ValorData: null, IdOpcao: null };
      switch (p.TipoResposta) {
        case "bool": r.ValorBool = !!v; break;
        case "int": case "decimal": r.ValorNumero = v === "" || v == null ? null : Number(v); break;
        case "selecao": case "multipla": r.IdOpcao = v ? Number(v) : null; break;
        // Data tem coluna própria (ValorData) — é o que o servidor valida e o relatório lê.
        case "data": r.ValorData = v || null; break;
        default: r.ValorTexto = v ?? null;
      }
      return r;
    });

  const vinculoFinal = () =>
    vinculoTipo === "RT" ? "Responsável/Tutor" : vinculoTipo === "OUTRO" ? (vinculoOutro || "Outro") : null;

  const montarDto = () => ({
    Ficha: { ...ficha },
    Responsavel: { ...responsavel, Parentesco: vinculoFinal() },
    ComposicaoFamiliar: familia,
    SaudeFamiliar: saude,
    Patrimonio: [...patrimonio, ...veiculos.map((v) => ({ ...v, Tipo: "Veiculo" }))]
      .map((p) => ({ ...p, QtdParcelasAberto: p.QtdParcelasAberto === "" ? null : Number(p.QtdParcelasAberto) })),
    Lancamentos: montarLancamentos(tiposR, tiposD, valoresLanc),
    Respostas: montarRespostas(),
  });

  /**
   * Erro da API em HTML legível. O backend responde { mensagem, campos } ou
   * { mensagem, documentos }; sem isto o Swal recebia o objeto cru e não mostrava nada.
   */
  const erroHtml = (e) => {
    const d = e?.response?.data;
    if (typeof d === "string" && d.trim()) return d;
    if (d && typeof d === "object") {
      const itens = [].concat(d.campos || [], d.documentos || []).filter(Boolean);
      const base = d.mensagem || d.title || "Não foi possível concluir a operação.";
      return itens.length ? `${base}<br/><br/>• ${itens.join("<br/>• ")}` : base;
    }
    if (e?.response?.status === 403) return "Você não tem permissão para enviar esta ficha.";
    if (e?.response?.status) return `O servidor respondeu com erro ${e.response.status}.`;
    return e?.message || "Erro inesperado.";
  };

  const salvarRascunho = async () => {
    try { await salvarMinhaFicha(montarDto()); Swal.fire("Salvo", "Rascunho salvo. Você pode continuar depois.", "success"); }
    catch (e) { Swal.fire({ icon: "error", title: "Erro", html: erroHtml(e) }); }
  };

  const enviar = async () => {
    // Validação: rascunho é livre, mas enviar exige campos obrigatórios preenchidos.
    const pendentes = perguntasPendentes(perguntas, respostas, byCodigo);
    const faltaResp = !responsavel.Nome?.trim();
    if (faltaResp || pendentes.length) {
      let alvo = 0;
      if (!faltaResp && pendentes.length) {
        const idx = tabs.findIndex((t) => t.label === (pendentes[0].Secao || "Geral"));
        if (idx >= 0) alvo = idx;
      }
      setAba(alvo);
      const itens = (faltaResp ? ["Responsável: Nome"] : [])
        .concat(pendentes.slice(0, 8).map((p) => `${p.Secao || ""}: ${p.Enunciado}`));
      Swal.fire({
        icon: "warning", title: "Faltam campos obrigatórios",
        html: `Preencha antes de enviar:<br/><br/>• ${itens.join("<br/>• ")}${pendentes.length > 8 ? "<br/>• …e outros" : ""}`,
      });
      return;
    }

    // Documentos obrigatórios: precisa de ao menos um arquivo em cada.
    const docsFaltando = tiposDoc.filter((t) => t.Obrigatorio && !anexos.some((a) => a.IdTipoDocumento === t.Id));
    if (docsFaltando.length) {
      const idxDoc = tabs.findIndex((t) => t.label === "Documentos");
      if (idxDoc >= 0) setAba(idxDoc);
      Swal.fire({
        icon: "warning", title: "Documentos obrigatórios faltando",
        html: `Anexe estes documentos antes de enviar:<br/><br/>• ${docsFaltando.map((t) => t.Descricao).join("<br/>• ")}`,
      });
      return;
    }

    const r = await Swal.fire({ title: "Enviar inscrição?", text: "Após enviar, a ficha será encaminhada para análise.", icon: "question", showCancelButton: true, confirmButtonText: "Enviar", cancelButtonText: "Revisar" });
    if (!r.isConfirmed) return;
    try {
      await salvarMinhaFicha(montarDto());
      await enviarMinhaFicha(ficha.Id);
      setEnviada(true);
      Swal.fire("Enviada!", "Sua inscrição foi enviada com sucesso.", "success");
    } catch (e) { Swal.fire({ icon: "error", title: "Não foi possível enviar", html: erroHtml(e) }); }
  };

  const onUploadDoc = async (tipo, arquivo) => {
    try { await uploadAnexoCand(ficha.Id, arquivo, tipo.Descricao, tipo.Id); await carregarAnexos(ficha.Id); }
    catch (e) { Swal.fire({ icon: "error", title: "Erro", html: erroHtml(e) }); }
  };
  const onExcluirDoc = async (a) => {
    const r = await Swal.fire({ title: "Remover documento?", icon: "warning", showCancelButton: true, confirmButtonText: "Remover", cancelButtonText: "Cancelar" });
    if (!r.isConfirmed) return;
    try { await excluirAnexoCand(a.Id); await carregarAnexos(ficha.Id); }
    catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e?.response?.data || e.message, "error"); }
  };

  if (!ficha) return <Typography>Carregando ficha…</Typography>;
  const bloqueado = enviada;

  // Helpers de seção do questionário
  const secaoLista = (nome) => secoes.find(([s]) => s === nome)?.[1] || [];
  const qTab = (nome) => {
    const lista = secaoLista(nome);
    if (!lista.length) return null;
    return {
      label: nome,
      pendente: perguntasPendentes(lista, respostas, byCodigo).length > 0,
      node: <QuestionarioCampos perguntas={lista} respostas={respostas} setResp={setResp} byCodigo={byCodigo} />,
    };
  };
  const habitacional = secaoLista("Situação Habitacional");

  // Mostra a lista de veículos quando a pergunta "Possui veículos automotores?" = sim.
  const perguntaVeic = byCodigo["hab_veiculos"];
  const possuiVeiculo = (() => {
    if (!perguntaVeic) return false;
    const op = (perguntaVeic.Opcoes || []).find((o) => String(o.Id) === String(respostas[perguntaVeic.Id]));
    return op?.Valor === "sim";
  })();

  const tabResponsavel = {
    label: "Responsável Financeiro", pendente: !responsavel.Nome?.trim(),
    node: (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth required label="Nome" value={responsavel.Nome || ""} onChange={(e) => setResponsavel({ ...responsavel, Nome: e.target.value })} /></Grid>
        <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth label="CPF" value={responsavel.Cpf || ""} onChange={(e) => setResponsavel({ ...responsavel, Cpf: e.target.value })} /></Grid>
        <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth label="RG" value={responsavel.Rg || ""} onChange={(e) => setResponsavel({ ...responsavel, Rg: e.target.value })} /></Grid>
        <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth label="Tel. residencial" value={responsavel.Telefone || ""} onChange={(e) => setResponsavel({ ...responsavel, Telefone: e.target.value })} /></Grid>
        <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth label="Tel. celular" value={responsavel.Celular || ""} onChange={(e) => setResponsavel({ ...responsavel, Celular: e.target.value })} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField select fullWidth label="Vínculo com o aluno" value={vinculoTipo} onChange={(e) => setVinculoTipo(e.target.value)}>
            <MenuItem value="RT">Responsável/Tutor</MenuItem>
            <MenuItem value="OUTRO">Outro</MenuItem>
          </TextField>
        </Grid>
        {vinculoTipo === "OUTRO" && (
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth label="Especificar vínculo" value={vinculoOutro} onChange={(e) => setVinculoOutro(e.target.value)} />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}><TextField fullWidth label="E-mail" value={responsavel.Email || ""} onChange={(e) => setResponsavel({ ...responsavel, Email: e.target.value })} /></Grid>
      </Grid>
    ),
  };

  const tabFamilia = {
    label: "Composição Familiar",
    node: (
      <>
        <BotaoAdd onClick={() => setFamilia([...familia, novoFamiliar()])} />
        {familia.map((m, i) => (
          <Stack key={i} direction="row" spacing={1} mt={1} useFlexGap flexWrap="wrap" alignItems="center">
            <TextField label="Nome" sx={{ flex: "1 1 160px" }} value={m.Nome || ""} onChange={(e) => upd(familia, setFamilia, i, "Nome", e.target.value)} />
            <TextField label="Parentesco" sx={{ width: 130 }} value={m.Parentesco || ""} onChange={(e) => upd(familia, setFamilia, i, "Parentesco", e.target.value)} />
            <TextField label="Idade" type="number" sx={{ width: 80 }} value={m.Idade || ""} onChange={(e) => upd(familia, setFamilia, i, "Idade", e.target.value)} />
            <TextField label="Est. civil" sx={{ width: 120 }} value={m.EstadoCivil || ""} onChange={(e) => upd(familia, setFamilia, i, "EstadoCivil", e.target.value)} />
            <TextField label="Escolaridade" sx={{ width: 150 }} value={m.Escolaridade || ""} onChange={(e) => upd(familia, setFamilia, i, "Escolaridade", e.target.value)} />
            <TextField label="Profissão" sx={{ width: 150 }} value={m.Profissao || ""} onChange={(e) => upd(familia, setFamilia, i, "Profissao", e.target.value)} />
            <CampoMoeda label="Salário" sx={{ width: 140 }} value={m.SalarioBruto} onChange={(n) => upd(familia, setFamilia, i, "SalarioBruto", n)} />
            <IconButton color="error" onClick={() => del(familia, setFamilia, i)}><DeleteIcon /></IconButton>
          </Stack>
        ))}
        {familia.length === 0 && <Vazio texto="Nenhum membro adicionado." />}
      </>
    ),
  };

  const tabGrupo = {
    label: "Grupo Familiar",
    pendente: perguntasPendentes(habitacional, respostas, byCodigo).length > 0,
    node: (
      <>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Saúde do grupo familiar</Typography>
        <BotaoAdd onClick={() => setSaude([...saude, novaSaude()])} />
        {saude.map((s, i) => (
          <Stack key={i} direction="row" spacing={1} mt={1}>
            <TextField label="Nome" sx={{ flex: 2 }} value={s.NomeCompleto || ""} onChange={(e) => upd(saude, setSaude, i, "NomeCompleto", e.target.value)} />
            <TextField label="Parentesco" sx={{ width: 130 }} value={s.Parentesco || ""} onChange={(e) => upd(saude, setSaude, i, "Parentesco", e.target.value)} />
            <TextField label="Doença crônica" sx={{ flex: 1 }} value={s.DoencaCronica || ""} onChange={(e) => upd(saude, setSaude, i, "DoencaCronica", e.target.value)} />
            <TextField label="Deficiência" sx={{ flex: 1 }} value={s.Deficiencia || ""} onChange={(e) => upd(saude, setSaude, i, "Deficiencia", e.target.value)} />
            <IconButton color="error" onClick={() => del(saude, setSaude, i)}><DeleteIcon /></IconButton>
          </Stack>
        ))}
        {saude.length === 0 && <Vazio texto="Nenhum registro de saúde." />}
        {habitacional.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Situação habitacional</Typography>
            <QuestionarioCampos perguntas={habitacional} respostas={respostas} setResp={setResp} byCodigo={byCodigo} />
            {possuiVeiculo && (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Veículos (modelo e ano)</Typography>
                  <BotaoAdd onClick={() => setVeiculos([...veiculos, novoVeiculo()])} />
                </Stack>
                {veiculos.map((v, i) => (
                  <Stack key={i} direction="row" spacing={1} mt={1}>
                    <TextField label="Modelo" sx={{ flex: 2 }} value={v.Descricao || ""} onChange={(e) => upd(veiculos, setVeiculos, i, "Descricao", e.target.value)} />
                    <TextField label="Ano" sx={{ width: 110 }} value={v.Ano || ""} onChange={(e) => upd(veiculos, setVeiculos, i, "Ano", e.target.value)} />
                    <IconButton color="error" onClick={() => del(veiculos, setVeiculos, i)}><DeleteIcon /></IconButton>
                  </Stack>
                ))}
                {veiculos.length === 0 && <Vazio texto="Adicione os veículos." />}
              </Box>
            )}
          </Box>
        )}
      </>
    ),
  };

  const tabReceitas = {
    label: "Receitas e Despesas",
    node: <LancamentosFixos tiposR={tiposR} tiposD={tiposD} valores={valoresLanc} setValores={setValoresLanc} />,
  };

  const tabBens = {
    label: "Bens Patrimoniais",
    node: (
      <>
        <BotaoAdd onClick={() => setPatrimonio([...patrimonio, novoPatrimonio()])} />
        {patrimonio.map((p, i) => (
          <Stack key={i} direction="row" spacing={1} mt={1}>
            <TextField select label="Tipo" sx={{ width: 120 }} value={p.Tipo || "Imovel"} onChange={(e) => upd(patrimonio, setPatrimonio, i, "Tipo", e.target.value)}>
              <MenuItem value="Imovel">Imóvel</MenuItem><MenuItem value="Movel">Móvel</MenuItem>
            </TextField>
            <TextField label="Descrição" sx={{ flex: 2 }} value={p.Descricao || ""} onChange={(e) => upd(patrimonio, setPatrimonio, i, "Descricao", e.target.value)} />
            <CampoMoeda label="Valor" sx={{ width: 150 }} value={p.ValorMercado} onChange={(n) => upd(patrimonio, setPatrimonio, i, "ValorMercado", n)} />
            <IconButton color="error" onClick={() => del(patrimonio, setPatrimonio, i)}><DeleteIcon /></IconButton>
          </Stack>
        ))}
        {patrimonio.length === 0 && <Vazio texto="Nenhum bem informado." />}
      </>
    ),
  };

  // Ordem das abas espelhando o sistema antigo.
  const tabs = [
    qTab("Instituição de Ensino de Origem"),
    qTab("Filiação"),
    tabResponsavel,
    tabFamilia,
    tabGrupo,
    tabReceitas,
    tabBens,
    qTab("Informações Complementares"),
    {
      label: "Documentos",
      pendente: tiposDoc.some((t) => t.Obrigatorio && !anexos.some((a) => a.IdTipoDocumento === t.Id)),
      node: (
        <DocumentosChecklist
          modo="candidato" tipos={tiposDoc} anexos={anexos}
          onUpload={onUploadDoc} onExcluir={onExcluirDoc} fetchBlob={getAnexoBlobCand}
          onBaixar={(a) => baixarAnexoCand(a.Id, a.NomeOriginal)} />
      ),
    },
  ].filter(Boolean);

  const abaAtual = Math.min(aba, tabs.length - 1);

  return (
    <Box>
      <Box sx={{ opacity: bloqueado ? 0.75 : 1 }}>
        {enviada && <Alert severity="success" sx={{ mb: 2 }}>Sua inscrição já foi enviada. Para alterações, procure a instituição.</Alert>}
        {!aberta && !enviada && <Alert severity="warning" sx={{ mb: 2 }}>O período de inscrição está encerrado.</Alert>}

        <Paper sx={{ mb: 2 }}>
          <TabsBar tabs={tabs} value={abaAtual} onChange={setAba} />
          <Box sx={{ p: { xs: 2, sm: 3 } }}>{tabs[abaAtual].node}</Box>
        </Paper>

        {!bloqueado && (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={salvarRascunho}>Salvar rascunho</Button>
            <Button variant="contained" onClick={enviar}>Enviar inscrição</Button>
          </Stack>
        )}
      </Box>

      {/* Recurso contra o resultado — só aparece se a edição habilitar (auto-resolvido no componente). */}
      <RecursoCandidato idFicha={ficha.Id} />
    </Box>
  );
}
